import React, { useEffect, useRef, useState } from "react";

import "./ThinkingIndicator.css";

/** Label shown from the moment a prompt is submitted (ADO 16799). */
export const THINKING_LABEL = "Thinking…";

/** Keep in sync with the animation duration in ThinkingIndicator.css. */
export const TRANSITION_MS = 260;

/**
 * Resolves the status label for an in-flight assistant turn.
 *
 * The pill starts at "Thinking…" and then narrates progress: every time the
 * agent reports a new tool call, the label advances to that tool's title. So a
 * run reads as Thinking… → "Look up leases" → "Summarise portfolio" → …
 * rather than sitting on a generic placeholder for the whole run.
 */
export function resolveThinkingLabel(message) {
  const steps = message?.steps;
  if (!Array.isArray(steps)) return THINKING_LABEL;

  // Steps are appended in order and so are the tool calls inside them, so the
  // last tool call of the last populated step is the most recent one. Steps
  // with no tool calls are skipped — they'd otherwise reset the label.
  let latest = null;
  for (const step of steps) {
    const calls = Array.isArray(step?.toolCalls) ? step.toolCalls : [];
    for (const call of calls) latest = call;
  }

  if (!latest) return THINKING_LABEL;
  return latest.title || latest.name || THINKING_LABEL;
}

/**
 * The gray "Thinking…" pill rendered next to the assistant avatar while the
 * backend is working (Figma "'Thinking' feedback", ADO 16799).
 *
 * When `label` changes, the outgoing text slides up and out while the incoming
 * text slides in from below, so the pill reads as a single status line that
 * advances rather than a value that snaps. The outgoing copy is
 * `aria-hidden` so the `role="status"` region announces only the current label.
 */
const ThinkingIndicator = ({ label = THINKING_LABEL }) => {
  const [current, setCurrent] = useState(label);
  const [outgoing, setOutgoing] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (label === current) return;
    setOutgoing(current);
    setCurrent(label);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOutgoing(null), TRANSITION_MS);
  }, [label, current]);

  // A run can finish (or be cancelled) mid-transition; don't set state after
  // the pill unmounts.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div className="thinking-indicator" role="status" aria-live="polite">
      <span className="thinking-indicator__viewport">
        {outgoing !== null && (
          <span
            key={`out-${outgoing}`}
            className="thinking-indicator__label thinking-indicator__label--exit"
            aria-hidden="true"
          >
            {outgoing}
          </span>
        )}
        <span
          key={`in-${current}`}
          className={`thinking-indicator__label${
            outgoing !== null ? " thinking-indicator__label--enter" : ""
          }`}
        >
          {current}
        </span>
      </span>
    </div>
  );
};

export default ThinkingIndicator;
